import React, { useState, useEffect } from "react";
import { listDepartments } from "../../../../service/apiService";
import { useUser } from "../../../../context/UserContext";
import { toast } from "sonner";

interface Department {
  id: string;
  department_name: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface DepartmentFilterButtonsProps {
  selectedDepartment: string | null;
  onDepartmentChange: (departmentName: string | null) => void;
  className?: string;
}

const DepartmentFilterButtons: React.FC<DepartmentFilterButtonsProps> = ({
  selectedDepartment,
  onDepartmentChange,
  className = ""
}) => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const { userId } = useUser();

  // Fetch departments on component mount
  useEffect(() => {
    const fetchDepartments = async () => {
      if (!userId) return;
      
      try {
        setLoading(true);
        const response = await listDepartments();
        
        if (response && response.departments) {
          // Sort departments alphabetically by name
          const sortedDepartments = response.departments.sort((a, b) => 
            a.department_name.localeCompare(b.department_name)
          );
          setDepartments(sortedDepartments);
        } else {
          setDepartments([]);
        }
      } catch (error) {
        console.error("Error fetching departments:", error);
        toast.error("Failed to load departments");
        setDepartments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDepartments();
  }, [userId]);

  const handleDepartmentClick = (departmentName: string | null) => {
    onDepartmentChange(departmentName);
  };

  if (loading) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <div className="animate-pulse flex space-x-2">
          <div className="h-8 bg-gray-200 rounded-full px-4 py-2 w-16"></div>
          <div className="h-8 bg-gray-200 rounded-full px-4 py-2 w-20"></div>
          <div className="h-8 bg-gray-200 rounded-full px-4 py-2 w-24"></div>
        </div>
      </div>
    );
  }

  if (departments.length === 0) {
    // Only show "All" button if no departments exist
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <button
          onClick={() => handleDepartmentClick(null)}
          className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
            selectedDepartment === null
              ? 'bg-blue-100 text-blue-700 border border-blue-200'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
          }`}
        >
          All
        </button>
      </div>
    );
  }

  return (
    <div className={`flex items-center space-x-2 overflow-x-auto ${className}`}>
      {/* All Departments Button */}
      <button
        onClick={() => handleDepartmentClick(null)}
        className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap ${
          selectedDepartment === null
            ? 'bg-blue-100 text-blue-700 border border-blue-200'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
        }`}
      >
        All
      </button>

      {/* Individual Department Buttons */}
      {departments.map((department) => (
        <button
          key={department.id}
          onClick={() => handleDepartmentClick(department.department_name)}
          className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap ${
            selectedDepartment === department.department_name
              ? 'bg-blue-100 text-blue-700 border border-blue-200'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
          }`}
        >
          {department.department_name}
        </button>
      ))}
    </div>
  );
};

export default DepartmentFilterButtons;